import React, { useState, useEffect } from "react";
import { Leaf, Car, Utensils, BedDouble, Zap, Wifi, Save, Sprout, FileText, HeartHandshake, TreePine } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, NoEvent, KpiCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client from "@/lib/api";
import { toast } from "sonner";

const TRANSPORT = { voiture: 0.193, train: 0.014, avion_court: 0.258, avion_long: 0.152, bus: 0.103, covoiturage: 0.064, velo_marche: 0.0 };
const TRANSPORT_LABEL = { voiture: "Voiture", train: "Train", avion_court: "Avion court-courrier", avion_long: "Avion long-courrier", bus: "Bus / Car", covoiturage: "Covoiturage", velo_marche: "Vélo / Marche" };
const F = { meal: 4.5, meal_veg: 1.6, hotel: 10.0, energy: 0.052, digital: 0.055, paper: 1.3 };
const EMPTY = { transportMode: "train", avgDistance: "", attendees: "", meals: "", vegMeals: "", hotelNights: "", energyKwh: "", digitalHours: "", paper: "" };
const CHART = ["#10b981", "#f59e0b", "#6366f1", "#3b82f6", "#ec4899"];

export default function Carbon() {
  const { selectedEvent } = useData();
  const [d, setD] = useState(EMPTY);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selectedEvent) return;
    setSaved(false);
    client.get(`/carbon/${selectedEvent.id}`).then(({ data }) => {
      if (data && Object.keys(data).length) setD({ ...EMPTY, ...data });
      else setD({ ...EMPTY, attendees: selectedEvent.maxParticipants || "" });
    });
  }, [selectedEvent]);

  if (!selectedEvent) return (<><PageHeader title="Bilan carbone" /><NoEvent /></>);

  const n = (x) => Number(x || 0);
  const transport = n(d.attendees) * n(d.avgDistance) * 2 * (TRANSPORT[d.transportMode] ?? 0);
  const food = n(d.meals) * F.meal + n(d.vegMeals) * F.meal_veg;
  const lodging = n(d.hotelNights) * F.hotel;
  const energy = n(d.energyKwh) * F.energy;
  const digital = n(d.digitalHours) * F.digital;
  const paper = n(d.paper) * F.paper;
  const totalKg = transport + food + lodging + energy + digital + paper;
  const totalT = totalKg / 1000;
  const perПart = n(d.attendees) ? totalKg / n(d.attendees) : 0;
  const trees = Math.ceil(totalKg / 25);

  const breakdown = [
    { name: "Transport", value: Math.round(transport) },
    { name: "Restauration", value: Math.round(food) },
    { name: "Hébergement", value: Math.round(lodging) },
    { name: "Énergie du lieu", value: Math.round(energy) },
    { name: "Numérique", value: Math.round(digital) },
    { name: "Papier", value: Math.round(paper) },
  ].filter((x) => x.value > 0);

  const save = async () => {
    await client.put(`/carbon/${selectedEvent.id}`, { ...d, totalKg: Math.round(totalKg), perParticipant: Math.round(perПart) });
    setSaved(true); toast.success("Bilan carbone enregistré");
  };

  const num = (k, label, icon, unit) => (
    <div><Label className="mb-1.5 flex items-center gap-1.5">{icon}{label}</Label><Input type="number" data-testid={`carbon-${k}`} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} placeholder={unit} /></div>
  );

  return (
    <>
      <PageHeader title="Bilan carbone" subtitle="Estimez l'empreinte carbone de votre événement" actions={<div className="flex items-center gap-2 flex-wrap"><Button variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid="carbon-rse-top" onClick={() => toast.success("Un cabinet de conseil RSE partenaire vous contactera pour un accompagnement")}><HeartHandshake className="h-4 w-4 mr-2" />Être suivi par un cabinet RSE</Button><EventSelector /></div>} />

      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 mb-6 flex items-center gap-6 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Leaf className="h-8 w-8" /></div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-emerald-100 text-sm uppercase tracking-wide font-medium">Émissions totales estimées</p>
          <p className="font-display text-4xl font-extrabold" data-testid="carbon-total">{totalT.toFixed(2)} t CO₂e</p>
          <p className="text-emerald-100 text-sm mt-1">{Math.round(totalKg).toLocaleString("fr-FR")} kg · soit {trees} arbres à planter 🌳</p>
        </div>
        <div className="text-right">
          <p className="text-emerald-100 text-sm">Par participant</p>
          <p className="font-display text-2xl font-bold">{perПart.toFixed(1)} kg</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="space-y-6">
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Car className="h-4 w-4 text-emerald-600" />Transport des participants</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="mb-1.5 block">Mode de transport principal</Label>
                <Select value={d.transportMode} onValueChange={(v) => setD({ ...d, transportMode: v })}><SelectTrigger data-testid="carbon-transport"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(TRANSPORT).map((k) => <SelectItem key={k} value={k}>{TRANSPORT_LABEL[k]}</SelectItem>)}</SelectContent></Select></div>
              {num("avgDistance", "Distance moyenne (km, aller)", null, "km")}
              {num("attendees", "Nombre de participants", null, "pers.")}
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Utensils className="h-4 w-4 text-emerald-600" />Restauration & hébergement</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {num("meals", "Repas classiques servis", <Utensils className="h-3.5 w-3.5" />, "repas")}
              {num("vegMeals", "Repas végétariens servis", <Sprout className="h-3.5 w-3.5" />, "repas")}
              {num("hotelNights", "Nuitées d'hôtel", <BedDouble className="h-3.5 w-3.5" />, "nuits")}
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Zap className="h-4 w-4 text-emerald-600" />Énergie & numérique</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {num("energyKwh", "Consommation du lieu", <Zap className="h-3.5 w-3.5" />, "kWh")}
              {num("digitalHours", "Streaming / visio", <Wifi className="h-3.5 w-3.5" />, "heures")}
              {num("paper", "Papier imprimé", <FileText className="h-3.5 w-3.5" />, "kg")}
            </div>
          </div>

          <Button onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-carbon"><Save className="h-4 w-4 mr-2" />Enregistrer le bilan carbone</Button>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="carbon-offset" onClick={() => toast.success(`Demande de compensation de ${Math.round(totalKg)} kg CO₂e enregistrée — un partenaire certifié vous recontactera`)}><TreePine className="h-4 w-4 mr-2" />Compenser mon empreinte</Button>
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="carbon-rse" onClick={() => toast.success("Un cabinet de conseil RSE partenaire vous contactera pour un accompagnement")}><HeartHandshake className="h-4 w-4 mr-2" />Être suivi par un cabinet RSE</Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <KpiCard icon={Leaf} tint="emerald" label="Total kg CO₂e" value={Math.round(totalKg).toLocaleString("fr-FR")} />
            <KpiCard icon={Sprout} tint="emerald" label="Arbres équivalents" value={trees} sub="pour compenser" />
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="font-display font-semibold mb-4">Répartition des émissions</h3>
            {breakdown.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{breakdown.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}</Pie><Tooltip formatter={(v) => `${v} kg`} /><Legend /></PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">Renseignez les données pour voir le bilan.</p>}
          </div>
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="font-display font-semibold mb-4">Émissions par poste (kg)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={breakdown} layout="vertical"><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => `${v} kg`} /><Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
